'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { Search, CheckSquare, Calendar, ArrowUpDown, ExternalLink, CheckCheck, Check } from 'lucide-react';
import { PROJECTS, LOGGED_IN_USER, type Task, type TaskStatus, type Priority } from '@/lib/mockData';
import PriorityBadge from '@/components/ui/PriorityBadge';
import StatusBadge from '@/components/ui/StatusBadge';
import EmptyState from '@/components/ui/EmptyState';
import { toast } from 'sonner';

type StatusFilter = 'All' | TaskStatus;
type PriorityFilter = 'All' | Priority;
type DateFilter = 'All' | 'Overdue' | 'Due Today' | 'This Week';
type SortKey = 'dueDate' | 'priority' | 'status' | 'project';

const PRIORITY_ORDER: Record<Priority, number> = { High: 0, Medium: 1, Low: 2 };
const STATUS_ORDER: Record<TaskStatus, number> = { 'To Do': 0, 'In Progress': 1, 'In Review': 2, Done: 3 };

export default function MyTasksContent() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('All');
  const [priorityFilter, setPriorityFilter] = useState<PriorityFilter>('All');
  const [dateFilter, setDateFilter] = useState<DateFilter>('All');
  const [sortKey, setSortKey] = useState<SortKey>('dueDate');
  const [sortAsc, setSortAsc] = useState(true);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [completedIds, setCompletedIds] = useState<Set<string>>(new Set());

  const TODAY = '2026-05-27';
  const WEEK_END = '2026-05-31';

  // Gather all tasks assigned to logged-in user
  const allMyTasks: (Task & { projectName: string })[] = PROJECTS.flatMap((p) =>
    p.tasks
      .filter((t) => t.assigneeId === LOGGED_IN_USER.id)
      .map((t) => ({ ...t, projectName: p.name }))
  );

  const filtered = useMemo(() => {
    let result = allMyTasks.filter((t) => {
      const matchSearch =
        t.title.toLowerCase().includes(search.toLowerCase()) ||
        t.projectName.toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === 'All' || t.status === statusFilter;
      const matchPriority = priorityFilter === 'All' || t.priority === priorityFilter;
      const matchDate =
        dateFilter === 'All'
          ? true
          : dateFilter === 'Overdue'
          ? t.dueDate < TODAY && t.status !== 'Done'
          : dateFilter === 'Due Today'
          ? t.dueDate === TODAY
          : t.dueDate >= TODAY && t.dueDate <= WEEK_END;
      return matchSearch && matchStatus && matchPriority && matchDate;
    });

    result = [...result].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'dueDate') cmp = a.dueDate.localeCompare(b.dueDate);
      else if (sortKey === 'priority') cmp = PRIORITY_ORDER[a.priority] - PRIORITY_ORDER[b.priority];
      else if (sortKey === 'status') cmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      else if (sortKey === 'project') cmp = a.projectName.localeCompare(b.projectName);
      return sortAsc ? cmp : -cmp;
    });

    return result;
  }, [allMyTasks, search, statusFilter, priorityFilter, dateFilter, sortKey, sortAsc]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(true); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filtered.map((t) => t.id)));
  };

  const markComplete = (id: string) => {
    setCompletedIds((prev) => new Set([...prev, id]));
    toast.success('Task marked as complete');
  };

  const bulkComplete = () => {
    setCompletedIds((prev) => new Set([...prev, ...selectedIds]));
    toast.success(`${selectedIds.size} tasks marked as complete`);
    setSelectedIds(new Set());
  };

  const isOverdue = (date: string) => date < TODAY;
  const isDueToday = (date: string) => date === TODAY;

  const statusFilters: StatusFilter[] = ['All', 'To Do', 'In Progress', 'In Review', 'Done'];
  const priorityFilters: PriorityFilter[] = ['All', 'High', 'Medium', 'Low'];
  const dateFilters: DateFilter[] = ['All', 'Overdue', 'Due Today', 'This Week'];

  const SortHeader = ({ label, key }: { label: string; key: SortKey }) => (
    <button
      onClick={() => toggleSort(key)}
      className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors group"
    >
      {label}
      <ArrowUpDown
        size={11}
        className={`transition-colors ${sortKey === key ? 'text-primary' : 'text-muted-foreground/50 group-hover:text-muted-foreground'}`}
      />
    </button>
  );

  return (
    <div className="p-6 lg:p-8 max-w-screen-2xl mx-auto fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">My Tasks</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {allMyTasks.length} tasks assigned to you across {PROJECTS.length} projects
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-muted-foreground bg-card border border-border rounded-lg px-3 py-1.5">
          <CheckCheck size={14} className="text-green-500" />
          <span className="font-medium text-foreground">{completedIds.size}</span> completed this session
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-card rounded-xl border border-border p-4 mb-5 space-y-3">
        {/* Search */}
        <div className="relative max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search tasks or projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-lg bg-background outline-none focus:ring-2 focus:ring-primary/30 transition-all placeholder:text-muted-foreground"
          />
        </div>

        {/* Filter chips row */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Status */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Status:</span>
            {statusFilters.map((f) => (
              <button
                key={`sf-${f}`}
                onClick={() => setStatusFilter(f)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                  statusFilter === f ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-border'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Priority */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Priority:</span>
            {priorityFilters.map((f) => (
              <button
                key={`pf-${f}`}
                onClick={() => setPriorityFilter(f)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                  priorityFilter === f ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-border'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          {/* Due Date */}
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-medium text-muted-foreground">Due:</span>
            {dateFilters.map((f) => (
              <button
                key={`df-${f}`}
                onClick={() => setDateFilter(f)}
                className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                  dateFilter === f ? 'bg-primary text-white' : 'bg-muted text-muted-foreground hover:bg-border'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-3 bg-primary/10 border border-primary/20 rounded-xl px-4 py-3 mb-4 slide-up">
          <span className="text-sm font-medium text-primary">
            {selectedIds.size} task{selectedIds.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex-1" />
          <button
            onClick={bulkComplete}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200 transition-colors"
          >
            <CheckCheck size={13} />
            Mark Complete
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Deselect all
          </button>
        </div>
      )}

      {/* Table */}
      {filtered.length === 0 ? (
        <EmptyState
          icon={<CheckSquare size={28} className="text-muted-foreground" />}
          title="No tasks found"
          description="No tasks match your current filters. Try adjusting the status, priority, or date range."
        />
      ) : (
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-[2rem_1fr_10rem_8rem_8rem_8rem] gap-3 px-4 py-3 border-b border-border bg-muted/40">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={selectedIds.size === filtered.length && filtered.length > 0}
                onChange={toggleAll}
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
              />
            </div>
            <SortHeader label="Task" key="dueDate" />
            <SortHeader label="Project" key="project" />
            <SortHeader label="Priority" key="priority" />
            <SortHeader label="Status" key="status" />
            <SortHeader label="Due Date" key="dueDate" />
          </div>

          {/* Table rows */}
          <div className="divide-y divide-border">
            {filtered.map((task) => {
              const isCompleted = completedIds.has(task.id) || task.status === 'Done';
              const overdue = isOverdue(task.dueDate) && !isCompleted;
              const today = isDueToday(task.dueDate);

              return (
                <div
                  key={`my-task-row-${task.id}`}
                  className={`grid grid-cols-[2rem_1fr_10rem_8rem_8rem_8rem] gap-3 px-4 py-3 items-center hover:bg-muted/40 transition-colors group ${
                    isCompleted ? 'opacity-50' : ''
                  }`}
                >
                  {/* Checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(task.id)}
                      onChange={() => toggleSelect(task.id)}
                      className="w-4 h-4 rounded border-border accent-primary cursor-pointer"
                    />
                  </div>

                  {/* Task name */}
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      onClick={() => markComplete(task.id)}
                      className={`w-4.5 h-4.5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all duration-150 ${
                        isCompleted
                          ? 'bg-green-500 border-green-500' :'border-border hover:border-primary/60'
                      }`}
                      style={{ width: '18px', height: '18px' }}
                      title={isCompleted ? 'Completed' : 'Mark as complete'}
                    >
                      {isCompleted && <Check size={10} className="text-white" />}
                    </button>
                    <Link
                      href="/task-detail"
                      className={`text-sm font-medium truncate hover:text-primary transition-colors ${
                        isCompleted ? 'line-through text-muted-foreground' : 'text-foreground'
                      }`}
                    >
                      {task.title}
                    </Link>
                    <ExternalLink
                      size={12}
                      className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                    />
                  </div>

                  {/* Project */}
                  <div className="min-w-0">
                    <span className="text-xs text-muted-foreground truncate block">{task.projectName}</span>
                  </div>

                  {/* Priority */}
                  <div>
                    <PriorityBadge priority={task.priority} size="sm" />
                  </div>

                  {/* Status */}
                  <div>
                    <StatusBadge status={isCompleted && task.status !== 'Done' ? 'Done' : task.status} />
                  </div>

                  {/* Due date */}
                  <div>
                    <div
                      className={`flex items-center gap-1 text-xs font-medium ${
                        overdue
                          ? 'text-red-500'
                          : today
                          ? 'text-amber-600' :'text-muted-foreground'
                      }`}
                    >
                      <Calendar size={11} />
                      {overdue ? 'Overdue' : today ? 'Today' : task.dueDate}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Table footer */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20">
            <span className="text-xs text-muted-foreground">
              Showing {filtered.length} of {allMyTasks.length} tasks
            </span>
            <div className="flex items-center gap-1">
              {[1].map((page) => (
                <button
                  key={`page-${page}`}
                  className="w-7 h-7 rounded-lg bg-primary text-white text-xs font-medium"
                >
                  {page}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}